/**
 * GENERATED_ASSET — one layout feeds paint, anchors and bounds, so they cannot disagree.
 * Design frame 384x320, y-down, origin bottom-center (192,320).
 * Painted ground contact is y=318: the outer edge of the base stroke (2px sampling padding).
 */
import type { FountainParams, FountainVariant } from './types';
import { Rng } from './rng';
import type { Pt } from './pathKit';

export const FRAME = { w: 384, h: 320 } as const;
export const CX = 192;
export const GROUND = 318;
export const BASE_STROKE = 2.1;
export const BASE_Y = GROUND - BASE_STROKE / 2; // path baseline; stroke outer edge lands on GROUND
export const SLAB_T = 9;   // visible slab edge thickness
export const SLAB_D = 12;  // visible slab top-surface depth
export const SLAB_OVER = 7;

export interface TierLayout {
  k: number; w: number; x0: number; x1: number;
  slabX0: number; slabX1: number; slabBack: number;
  s: number; edgeBottom: number; base: number;
  lipX: number; notchW: number; notchDepth: number;
  /** Where this tier's fall lands: on the slab below (k>0) or in the basin (k=0). */
  landing: Pt;
}
export interface Basin {
  x0: number; x1: number; rimTop: number; rimBottom: number; rimR: number; wallTop: number;
  openX0: number; openX1: number; openY0: number; openY1: number; openR: number;
}
export interface Stele {
  x0: number; x1: number; baseY: number; bodyTop: number; capX0: number; capX1: number;
  capTop: number; capFront: number; plateY: number; mouthY: number; jetLandY: number;
}
export interface WaterRegion { x0: number; y0: number; x1: number; y1: number; r: number; present: boolean }
export interface Layout {
  tiers: TierLayout[]; basin: Basin; stele: Stele; water: WaterRegion; T: 2 | 3; broken: boolean;
}

const WIDTHS: Record<2 | 3, number[]> = { 2: [196, 124], 3: [176, 122, 78] };
const WALL_H: Record<2 | 3, number[]> = { 2: [46, 40], 3: [36, 32, 28] };
const LIP_DX: Record<2 | 3, number[]> = { 2: [26, -22], 3: [26, -24, 20] };
const NOTCH_W = [46, 38, 30];

export function buildLayout(p: FountainParams, seed: number, variant: FountainVariant): Layout {
  const T = p.tiers;
  const rng = new Rng(seed, 'layout');
  const basin: Basin = {
    x0: 22, x1: 362, rimTop: 244, rimBottom: 298, rimR: 22, wallTop: 278,
    openX0: 34, openX1: 350, openY0: 256, openY1: 288, openR: 13,
  };
  const tiers: TierLayout[] = [];
  let base = 282;
  for (let k = 0; k < T; k++) {
    const w = WIDTHS[T][k]!;
    const s = base - WALL_H[T][k]! - SLAB_T;
    const notchW = NOTCH_W[k]!;
    const slabHalf = w / 2 + SLAB_OVER;
    const jitter = Math.round(rng.range(-3, 3) * 10) / 10;
    const lipX = CX + LIP_DX[T][k]! + jitter;
    tiers.push({
      k, w, x0: CX - w / 2, x1: CX + w / 2,
      slabX0: CX - slabHalf, slabX1: CX + slabHalf, slabBack: 5,
      s, edgeBottom: s + SLAB_T, base, lipX, notchW, notchDepth: 3,
      landing: { x: lipX, y: base + 3 },
    });
    base = s - SLAB_D;
  }
  const top = tiers[T - 1]!;
  const baseY = top.s - SLAB_D + 2;
  const bodyH = T === 3 ? 75 : 83;
  const half = T === 3 ? 29 : 31;
  const bodyTop = baseY - bodyH;
  const stele: Stele = {
    x0: CX - half, x1: CX + half, baseY, bodyTop,
    capX0: CX - half - 7, capX1: CX + half + 7,
    capTop: bodyTop - 14, capFront: bodyTop - 8,
    plateY: bodyTop + 28, mouthY: bodyTop + 50, jetLandY: baseY + 4,
  };
  // Water region: shrinks with waterLevel. Level 0 = no standing water.
  const L = p.waterLevel;
  const inset = (1 - L) * 38, drop = (1 - L) * 20;
  const x0 = basin.openX0 + inset, x1 = basin.openX1 - inset;
  const y0 = basin.openY0 + drop, y1 = basin.openY1 - (1 - L) * 2;
  const water: WaterRegion = {
    x0, y0, x1, y1, r: Math.max(2, Math.min(basin.openR, (y1 - y0) / 2 - 1)), present: L >= 0.04,
  };
  return { tiers, basin, stele, water, T, broken: variant === 'dry_ruin' };
}

/** Visible-water cut-out around the bottom tier's base so water never paints over stone. */
export function waterCut(L: Layout): { x0: number; x1: number; y: number } {
  const t = L.tiers[0]!;
  return { x0: t.x0, x1: t.x1, y: t.base + BASE_STROKE / 2 };
}
